import { useState, type ReactNode } from 'react';
import type {
  AuditEvent, ChatMessage, ChatStream, FinishedRun, OwnerCommand, Plan, Project,
  ReportMessage, SessionRef, Worker,
} from '../lib/data';
import { auditLine, heldBackCount, heldBackNotice, streamLabel, visibleAuditEvents } from '../lib/audit';
import { durationPhrase, handoverPhrase, runDurationMs, runTaskTitle, toolPhrase, workerPhrase } from '../lib/finished';
import { MERGED_AWAITING_OWNER, type OwnerGateRunView } from '../lib/owner-gate-waiting';
import { presentRecentRuns, type RecentRunView } from '../lib/recent-run-supersession';
import { plainTerminalText } from '../lib/terminal-plain-text';
import { runStats } from '../lib/runs';
import { readSupervisorAiPerspective } from '../lib/supervisor-ai-perspective';
import type { ProposalArchiveReason } from '../lib/proposal-archive';
import { PlanHistoryPanel } from './Plans';
import { ReadAloudButton } from './Voice';
import { WorkerConsole } from './WorkerConsole';
import { WorkerWorkload, WorkloadChart } from './Workload';

export interface EngineRoomHistoryProps {
  readonly results: readonly FinishedRun[];
  readonly workers: readonly Worker[];
  readonly reports: readonly ReportMessage[];
  readonly streams: readonly ChatStream[];
  readonly projects: readonly Project[];
  readonly sessions: readonly SessionRef[];
  readonly commands: readonly OwnerCommand[];
  readonly plans: readonly Plan[];
  readonly audit: readonly AuditEvent[];
  readonly auditChatter: readonly AuditEvent[];
  readonly streamId: string | null;
  readonly chat: readonly ChatMessage[];
  readonly titles: ReadonlyMap<string, string>;
  readonly nowMs: number;
  /** Archived proposals, classified with the same rows as the needs-you counts. */
  readonly archivedPlans?: ReadonlyMap<string, ProposalArchiveReason>;
  readonly onStreamChange: (streamId: string | null) => void;
  readonly ownerGateViews?: ReadonlyMap<string, OwnerGateRunView>;
}

/**
 * Engine Room History is the old Monitor Center's read-only evidence surface.
 * It owns no data and starts no work: every panel consumes the same bounded
 * rows already loaded by OperationsRoom.
 */
export function EngineRoomHistory({
  results, workers, reports, streams, projects, sessions, commands, plans,
  audit, auditChatter, streamId, chat, titles, nowMs, archivedPlans, onStreamChange, ownerGateViews,
}: EngineRoomHistoryProps) {
  return (
    <section className="work one engine-room-history" aria-label="Engine Room History">
      <header className="engine-history-intro">
        <div>
          <h2>History</h2>
          <small className="muted">What happened before — finished work, reports, conversations, plans, and audit evidence.</small>
        </div>
      </header>

      <HistoryPanel title="Workload">
        <small className="muted">Counted from the {results.length} finished runs Command holds - the newest fifty.</small>
        <WorkloadChart results={results} nowMs={nowMs} />
      </HistoryPanel>

      <HistoryPanel title="Workload by worker">
        <small className="muted">The same runs, one worker at a time - what each finished, how it went, and how long it takes them.</small>
        <WorkerWorkload results={results} workers={workers} nowMs={nowMs} />
      </HistoryPanel>

      <HistoryPanel title="Finished tasks" count={results.length}>
        {results.length > 0 && <small className="muted">{runStats(results, nowMs).label}</small>}
        <FinishedRunList results={results} workers={workers} titles={titles} ownerGateViews={ownerGateViews} />
      </HistoryPanel>

      <HistoryPanel title="Worker reports" count={reports.length}>
        <HistoryList
          items={reports}
          empty="No reports yet."
          render={(report) => <ReportLine report={report} />}
        />
      </HistoryPanel>

      <HistoryPanel title="Worker feed" closed>
        <select
          aria-label="Chat stream"
          value={streamId ?? ''}
          onChange={(event) => onStreamChange(event.target.value || null)}
        >
          <option value="">Select a stream</option>
          {streams.map((stream) => (
            <option key={stream.id} value={stream.id}>
              {streamLabel(stream, projects, sessions, workers)}
            </option>
          ))}
        </select>
        {streamId && <WorkerConsole workerNumber={null} messages={chat} empty="Nothing on this stream yet." />}
        <small className="muted">Read-only. Orders go through Main Command, so this history never becomes a second command path.</small>
      </HistoryPanel>

      <HistoryPanel title="Command history" count={commands.length} closed>
        <HistoryList
          items={commands}
          empty="No commands yet. Dictate or type an order in the Control Center."
          render={(command) => <><span className="seq">#{command.seq}</span> {command.content}</>}
        />
      </HistoryPanel>

      <HistoryPanel title="Plan history" count={plans.length} closed>
        <PlanHistoryPanel plans={plans} projects={projects} commands={commands} audit={audit} nowMs={nowMs} archived={archivedPlans} />
      </HistoryPanel>

      <HistoryPanel title="Audit" count={audit.length} closed>
        <HistoryAuditPanel meaningful={audit} routine={auditChatter} />
      </HistoryPanel>
    </section>
  );
}

function HistoryPanel({ title, count, closed, children }: {
  readonly title: string;
  readonly count?: number;
  readonly closed?: boolean;
  readonly children: ReactNode;
}) {
  return (
    <details className="panel" open={!closed}>
      <summary>
        <h2>{title}</h2>
        {count !== undefined && count > 0 && <span className="count">{count}</span>}
      </summary>
      <div className="panel-body">{children}</div>
    </details>
  );
}

/** A Supervisor AI perspective shows its structured advice and what actually ran; any other report keeps its one line. */
function ReportLine({ report }: { readonly report: ReportMessage }) {
  const read = readSupervisorAiPerspective(report.envelope);
  if (read.kind !== 'perspective') {
    return (
      <>
        <span className="tag">{report.envelope.reportKind ?? 'report'}</span>{' '}
        {report.envelope.summary ?? ''}{' '}
        {read.kind === 'unreadable' && <small className="muted">(AI perspective could not be read; details withheld) </small>}
        <ReadAloudButton text={report.envelope.summary ?? ''} />
      </>
    );
  }
  const view = read.view;
  return (
    <div className="supervisor-ai-perspective" aria-label={`${view.roleTitle} AI perspective`}>
      <span className="tag">{view.roleTitle} AI perspective</span>{' '}
      <small className="muted">Advisory only · confidence {view.confidence}</small>
      <p>{view.interpretation} <ReadAloudButton text={view.interpretation} /></p>
      {view.lines.length > 0 && (
        <ul>
          {view.lines.map((line, index) => <li key={index}><strong>{line.label}:</strong> {line.text}</li>)}
        </ul>
      )}
      <small className="muted">Actual: {view.actual} · playbook {view.playbookVersion}</small>
    </div>
  );
}

function HistoryList<T extends { id?: string; approval_id?: string }>({
  items, render, empty,
}: {
  readonly items: readonly T[];
  readonly render: (item: T) => ReactNode;
  readonly empty: string;
}) {
  if (items.length === 0) return <p className="muted">{empty}</p>;
  return <ul className="list">{items.map((item, index) => <li key={item.id ?? item.approval_id ?? index}>{render(item)}</li>)}</ul>;
}

function Status({ value }: { readonly value: string }) {
  return <span className={`status status-${value}`}>{value.replace('_', ' ')}</span>;
}

function LogView({ text, label }: { readonly text: string; readonly label: string }) {
  return <pre className="logview" tabIndex={0} role="group" aria-label={label}>{plainTerminalText(text)}</pre>;
}

function HistoryAuditPanel({ meaningful, routine }: {
  readonly meaningful: readonly AuditEvent[];
  readonly routine: readonly AuditEvent[];
}) {
  const [everything, setEverything] = useState(false);
  const rows = visibleAuditEvents(meaningful, routine, everything);
  const notice = heldBackNotice(heldBackCount(meaningful, routine, everything));

  return (
    <>
      <small className="muted">
        {everything
          ? 'Everything Command holds, newest first - the routine machine check-ins among it.'
          : 'What happened: plans, decisions, tasks, and computers coming and going.'}
      </small>
      <HistoryList
        items={rows}
        empty={everything ? 'No events yet.' : 'Nothing has happened yet beyond the routine check-ins.'}
        render={(event) => <AuditHistoryRow event={event} />}
      />
      <span className="row spread">
        <small className="muted">{notice ?? 'Nothing is being held back.'}</small>
        <button type="button" className="link" aria-pressed={everything} onClick={() => setEverything((on) => !on)}>
          {everything ? 'Show only what happened' : 'Show the check-ins too'}
        </button>
      </span>
    </>
  );
}

function AuditHistoryRow({ event }: { readonly event: AuditEvent }) {
  const line = auditLine(event);
  const decided = [
    line.gate === null ? null : `decided at ${line.gate}`,
    line.reason === null ? null : `because ${line.reason}`,
  ].filter((part) => part !== null);

  return (
    <div className="stack event">
      <span>{line.sentence}</span>
      {decided.length > 0 && <small className="muted">{decided.join(' · ')}</small>}
      {line.facts.length > 0 && (
        <dl className="facts">
          {line.facts.map((fact) => (
            <div key={fact.label}>
              <dt>{fact.label}</dt>
              <dd>{fact.value}</dd>
            </div>
          ))}
        </dl>
      )}
      {line.detail !== null && (
        <details>
          <summary className="muted">Everything this event recorded</summary>
          <LogView text={line.detail} label="Everything this event recorded" />
        </details>
      )}
    </div>
  );
}

function FinishedRunList({ results, workers, titles, ownerGateViews }: {
  readonly results: readonly FinishedRun[];
  readonly workers: readonly Worker[];
  readonly titles: ReadonlyMap<string, string>;
  readonly ownerGateViews?: ReadonlyMap<string, OwnerGateRunView>;
}) {
  const views = presentRecentRuns(results);
  return (
    <HistoryList
      items={results.map((result) => ({ ...result, id: result.assignment_id }))}
      empty="Finished tasks and their results appear here."
      render={(result) => (
        <RunHistoryResult
          result={result}
          workers={workers}
          titles={titles}
          view={views.get(result.assignment_id)}
          awaitingOwner={ownerGateViews?.get(result.assignment_id)?.awaitingOwner === true}
        />
      )}
    />
  );
}

function RunHistoryResult({ result, workers, titles, view, awaitingOwner }: {
  readonly result: FinishedRun;
  readonly workers: readonly Worker[];
  readonly titles: ReadonlyMap<string, string>;
  readonly view: RecentRunView | undefined;
  readonly awaitingOwner: boolean;
}) {
  const took = durationPhrase(runDurationMs(result));
  const passedOver = handoverPhrase(result);
  const summary = view?.summary ?? result.summary;

  return (
    <div className="stack">
      <span className="row spread">
        <strong>{runTaskTitle(result, titles)}</strong>
        {awaitingOwner
          ? <span className="status status-awaiting-owner">{MERGED_AWAITING_OWNER}</span>
          : <Status value={result.success ? 'completed' : 'failed'} />}
      </span>
      {view?.supersededByLabel !== null && view?.supersededByLabel !== undefined && (
        <small className="muted">{view.supersededByLabel}</small>
      )}
      {summary !== '' && <span>{summary}</span>}
      <small className="muted">
        {workerPhrase(result, workers)}{' · took '}{took}{' · ran with '}{toolPhrase(result)}
        {passedOver === null ? '' : ` (handed over; ${passedOver} was assigned)`}
      </small>
      {result.output_tail && (
        <details>
          <summary className="muted">What the worker printed</summary>
          <LogView text={result.output_tail} label="What the worker printed" />
        </details>
      )}
    </div>
  );
}
